// Turbo + ActionCable wiring for live Turbo Streams.
// Loaded as <script type="module"> so imports are native ESM.

import * as Turbo from "@hotwired/turbo";
import { createConsumer } from "@rails/actioncable";

const consumer = createConsumer("/cable");

// Define the custom element that turbo_stream_from generates.
// Mirrors the official turbo-rails TurboCableStreamSourceElement:
//   - Turbo.connectStreamSource(this) registers the element with Turbo's
//     StreamObserver so Turbo Drive's DOM lifecycle manages it correctly.
//   - received() dispatches a MessageEvent on the element (not renderStreamMessage
//     directly) so the registered StreamObserver handles rendering.
//   - Turbo.disconnectStreamSource(this) deregisters on removal.
class TurboCableStreamSourceElement extends HTMLElement {
  connectedCallback() {
    const signedStreamName = this.getAttribute("signed-stream-name");
    const element = this;

    Turbo.connectStreamSource(this);

    this.subscription = consumer.subscriptions.create(
      {
        channel: this.getAttribute("channel"),
        signed_stream_name: signedStreamName,
      },
      {
        connected() {
          // Mirror the official turbo-rails element: the [connected]
          // attribute is part of the <turbo-cable-stream-source> contract.
          // turbo-rails' system-test helper waits on it after every visit,
          // and tooling/CSS may key off it.
          element.setAttribute("connected", "");

          // Fires on initial subscribe AND every reconnect. Lets listeners
          // re-sync state that may have been broadcast while we were offline
          // or before the WebSocket handshake completed.
          element.dispatchEvent(
            new CustomEvent("turbo:cable-connected", {
              bubbles: true,
              detail: { signedStreamName },
            }),
          );
        },
        disconnected() {
          element.removeAttribute("connected");
        },
        received(data) {
          // Dispatch a MessageEvent on the element so Turbo's StreamObserver
          // (registered via connectStreamSource) handles rendering. Calling
          // renderStreamMessage directly bypasses the observer lifecycle.
          element.dispatchEvent(new MessageEvent("message", { data }));
        },
      },
    );
  }

  disconnectedCallback() {
    this.removeAttribute("connected");
    Turbo.disconnectStreamSource(this);
    this.subscription?.unsubscribe();
  }
}

if (!customElements.get("turbo-cable-stream-source")) {
  customElements.define("turbo-cable-stream-source", TurboCableStreamSourceElement);
}
